import { useTranslation } from 'react-i18next';
import { Stack, Text } from '@mantine/core';
import type { LoadingConditions } from '@/types/sell';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Одно условие погрузки: подпись и значение текстом. */
export interface LoadingConditionRow {
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

/**
 * Условия погрузки продавца строками «подпись — значение»: часы работы, суточная отгрузка, высота ТС, способы отгрузки,
 * оборудование базиса. Пустые условия не выводятся. Общий источник для страницы предложения (строки раздела) и превью
 * сделки (`LoadingConditionsSummary`), чтобы форматирование не расходилось.
 */
export function loadingConditionRows(lc: LoadingConditions, t: Translate): LoadingConditionRow[] {
  const rows: (LoadingConditionRow | null)[] = [
    lc.startTime && lc.endTime ? { key: 'hours', label: t('app.sell.loading.hours'), value: `${lc.startTime}–${lc.endTime}` } : null,
    lc.dailyShippingRate?.value != null
      ? {
          key: 'capacity',
          label: t('app.sell.loading.capacity'),
          value: `${lc.dailyShippingRate.value} ${t(
            lc.dailyShippingRate.type === 'TS' ? 'app.sell.loading.wagonsPerDay' : 'app.sell.loading.tonsPerDay',
          )}`,
        }
      : null,
    lc.maxVehicleHight != null
      ? { key: 'height', label: t('app.sell.loading.vehicleHeight'), value: `${lc.maxVehicleHight} ${t('app.sell.loading.meters')}` }
      : null,
    lc.shipmentModes?.length ? { key: 'modes', label: t('app.sell.loading.modes'), value: lc.shipmentModes.join(', ') } : null,
  ];
  const equipment = [
    lc.trailers ? t('app.sell.loading.equipTrailers') : null,
    lc.trawls ? t('app.sell.loading.equipTrawls') : null,
    lc.scalesLength != null ? t('app.sell.loading.equipScales', { length: lc.scalesLength }) : null,
  ].filter((item): item is string => Boolean(item));
  if (equipment.length > 0) {
    const text = equipment.join(', ');
    rows.push({ key: 'equipment', label: t('app.sell.loading.equipment'), value: text.charAt(0).toUpperCase() + text.slice(1) });
  }
  return rows.filter((row): row is LoadingConditionRow => row != null);
}

// Условия погрузки текстом «подпись: значение» для превью сделки (покупатель и продавец). Обёртку с подписью раздела
// даёт вызывающий.
export default function LoadingConditionsSummary({ lc }: { readonly lc: LoadingConditions }) {
  const { t } = useTranslation();
  return (
    <Stack gap={6}>
      {loadingConditionRows(lc, t).map((row) => (
        <Text key={row.key} size="sm" c="gray.8">
          {row.label}: {row.value}
        </Text>
      ))}
    </Stack>
  );
}
